import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownSection,
  DropdownItem,
  Button,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LLMModel } from "@webuild-ai/agent-platform/models/components";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useChatStore } from "@/stores/ChatStore";
import { getBestForDescription } from "@/utils/modelDescriptions";
import { isSpecializedWorkflow } from "@/utils/workflowUtils";

import { ModelIcon } from "./ModelIcon";

const ChatModelSelector = ({
  position = "top-end",
}: {
  position?:
    | "bottom-start"
    | "bottom-end"
    | "top-start"
    | "top-end"
    | "left-start"
    | "left-end"
    | "right-start"
    | "right-end";
}) => {
  const { chat } = useChatStore();
  const { getWorkflow, setWorkflow, workflow } = useWorkflowStore();
  const { platformClient } = useWebuildAPIClient();
  const { t } = useTranslation();
  const [models, setModels] = useState<LLMModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<LLMModel | null>(null);

  useEffect(() => {
    const fetchModels = async () => {
      if (!platformClient) return;
      const availableModels = await platformClient.models.list();
      // Sort models by providerId: openai, anthropic, google
      const providerOrder = ["openai", "anthropic", "google"];

      availableModels.sort((a: any, b: any) => {
        const aIndex = providerOrder.indexOf(a.providerId);
        const bIndex = providerOrder.indexOf(b.providerId);

        // If both are in the list, sort by their order
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        // If only a is in the list, it comes first
        if (aIndex !== -1) return -1;
        // If only b is in the list, it comes first
        if (bIndex !== -1) return 1;

        // Otherwise, keep original order
        return 0;
      });

      setModels(availableModels);

      // Set initial selected model
      const workflow = getWorkflow();

      if (!workflow?.agents) {
        setSelectedModel(availableModels[0]);

        return;
      }

      const agentWithModel = Object.values(workflow.agents).find(
        (agent) => agent.parameters?.model,
      );

      if (!agentWithModel) {
        setSelectedModel(availableModels[0]);

        return;
      }

      const model = availableModels.find((m: LLMModel) => m.id === agentWithModel.parameters.model);

      setSelectedModel(model || availableModels[0]);
    };

    fetchModels();
  }, [platformClient, getWorkflow]);

  useEffect(() => {
    if (!models.length) return;

    if (chat?.modelUsed) {
      const modelFromHistory = models.find((m: LLMModel) => m.id === chat.modelUsed);

      if (modelFromHistory) {
        setSelectedModel(modelFromHistory);

        return;
      }
    }

    const workflow = getWorkflow();

    if (!workflow?.agents) {
      setSelectedModel(models[0]);

      return;
    }

    const agentWithModel = Object.values(workflow.agents).find((agent) => agent.parameters?.model);

    if (!agentWithModel) {
      setSelectedModel(models[0]);

      return;
    }

    const model = models.find((m: LLMModel) => m.id === agentWithModel.parameters.model);

    setSelectedModel(model || models[0]);
  }, [workflow, models, chat]);

  const setWorkflowModel = (model: LLMModel) => {
    /**/
    const workflow = getWorkflow();

    if (!workflow?.agents) return;

    const updatedWorkflow = {
      ...workflow,
      agents: Object.fromEntries(
        Object.entries(workflow.agents).map(([id, agent]) => [
          id,
          {
            ...agent,
            parameters: {
              ...agent.parameters,
              model: model.id,
            },
          },
        ]),
      ),
    };

    setWorkflow(updatedWorkflow);
    setSelectedModel(model);
  };

  // Determine disable reasons
  const hasMessages = chat?.messages && chat?.messages.length > 0;
  const isSpecialized = isSpecializedWorkflow(workflow);
  const isDisabled = isSpecialized || hasMessages;

  // Determine tooltip message
  const tooltipMessage = isSpecialized
    ? t("disabled-model-workflow")
    : hasMessages
      ? t("disabled-model-selector")
      : "";

  return (
    <div className="flex items-center gap-2">
      {selectedModel && !isDisabled ? (
        <>
          <Dropdown data-id="model-selector-dropdown" placement={position}>
            <DropdownTrigger isDisabled={chat?.messages && chat?.messages.length > 0}>
              <Button
                className="flex-shrink-0 min-w-fit max-w-[100px] md:flex-1 light:bg-brand-secondary"
                data-id="model-selector-button"
                endContent={
                  <Icon
                    className="text-default-400"
                    height={20}
                    icon="solar:alt-arrow-down-linear"
                    width={20}
                  />
                }
                size="sm"
                startContent={
                  <ModelIcon
                    alt={selectedModel.name}
                    className="text-default-400"
                    height={20}
                    icon={
                      selectedModel.icon.match(/\.(png|jpe?g|svg)$/)
                        ? `/models/${selectedModel.icon}`
                        : selectedModel.icon
                    }
                    width={20}
                  />
                }
                variant="flat"
              >
                <span className="hidden sm:inline">{selectedModel.name}</span>
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Dropdown menu with icons"
              className="px-0 py-[16px]"
              data-id="model-selector-menu"
              style={{ maxHeight: "500px", overflowY: "auto" }}
              variant="faded"
            >
              <DropdownSection
                classNames={{
                  heading: "text-tiny px-[10px]",
                }}
                data-id="model-selector-section"
                title={t("Model")}
              >
                {models.map((model) => (
                  <DropdownItem
                    key={model.id}
                    className="text-default-500 data-[hover=true]:text-default-500"
                    classNames={{
                      description: "text-default-500 text-tiny",
                    }}
                    data-id={`model-item-${model.id}`}
                    description={model.description}
                    endContent={
                      model.id === selectedModel?.id && (
                        <Icon
                          className="text-default-foreground"
                          height={24}
                          icon="solar:check-circle-bold"
                          width={24}
                        />
                      )
                    }
                    startContent={
                      <ModelIcon
                        alt={model.name}
                        className="text-default-400"
                        height={24}
                        icon={
                          model.icon.match(/\.(png|jpe?g|svg)$/)
                            ? `/models/${model.icon}`
                            : model.icon
                        }
                        width={24}
                      />
                    }
                    textValue={model.name}
                    onPress={() => setWorkflowModel(model)}
                  >
                    {model.name}
                  </DropdownItem>
                ))}
              </DropdownSection>
            </DropdownMenu>
          </Dropdown>
          <Popover showArrow placement="bottom">
            <PopoverTrigger>
              <Button
                isIconOnly
                aria-label="Model information"
                className="min-w-fit"
                size="sm"
                variant="light"
              >
                <Icon
                  className="text-default-400"
                  height={16}
                  icon="solar:info-circle-linear"
                  width={16}
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="max-w-[320px] p-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-small text-brand-primary">
                  {selectedModel.name}
                </h4>
                <p className="text-tiny text-default-600">
                  {getBestForDescription(selectedModel.name, t)}
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </>
      ) : (
        <>
          <Tooltip
            showArrow
            className="max-w-[280px]"
            content={tooltipMessage}
            placement="bottom-start"
            size="sm"
          >
            <Button
              disabled
              className="
    flex-shrink-0 min-w-fit max-w-[100px] md:flex-1
    light:bg-brand-secondary
    hover:bg-brand-secondary focus:bg-brand-secondary active:bg-brand-secondary
    disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed
  "
              data-id="model-selector-button"
              endContent={
                <Icon
                  className="text-default-400"
                  height={20}
                  icon="solar:alt-arrow-down-linear"
                  width={20}
                />
              }
              size="sm"
              startContent={
                <ModelIcon
                  alt={selectedModel?.name}
                  className="text-default-400"
                  height={20}
                  icon={
                    selectedModel?.icon.match(/\.(png|jpe?g|svg)$/)
                      ? `/models/${selectedModel?.icon}`
                      : (selectedModel?.icon as string)
                  }
                  width={20}
                />
              }
              variant="flat"
            >
              <span className="hidden sm:inline">{selectedModel?.name}</span>
            </Button>
          </Tooltip>
          <Popover showArrow placement="bottom">
            <PopoverTrigger>
              <Button
                disabled
                isIconOnly
                aria-label="Model information"
                className="min-w-fit"
                size="sm"
                variant="light"
              >
                <Icon
                  className="text-default-400"
                  height={16}
                  icon="solar:info-circle-linear"
                  width={16}
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="max-w-[320px] p-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-small text-brand-primary">
                  {selectedModel?.name}
                </h4>
                <p className="text-tiny text-default-600">
                  {selectedModel ? getBestForDescription(selectedModel.name, t) : ""}
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </>
      )}
    </div>
  );
};

export { ChatModelSelector };
